import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DynamicTableColumnConfig,
  DynamicTableConfig,
  DynamicTableRowConfig,
  Question,
} from '../../config/types';
import { interpolateQuestionText } from '../../utils/form-utils';
import {
  calculateDynamicTableSizing,
  positionBoundedGroupLabels,
  filterDynamicTableRows,
  getDynamicTableFieldName,
  getDynamicTableRowHeaderFieldName,
  getSizingKey,
  GROUP_SEPARATOR_WIDTH,
  resolveContinuousDynamicTableConfig,
  resolveDynamicTableRowsFromAnswer,
  Table,
  TableCaption,
  TableRegion,
  TableViewport,
  type DynamicTableSizing,
} from './DynamicTable';
import { formatDynamicTableCellValue } from './dynamic-table-derived-values';

type Props = {
  question: Question;
  answer: unknown;
  allAnswers?: Record<string, unknown>;
};

const getValues = (answer: unknown): Record<string, string> => {
  if (typeof answer === 'string') {
    try {
      const parsed = JSON.parse(answer);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key, String(value ?? '')]))
        : {};
    } catch (_error) {
      return {};
    }
  }

  return answer && typeof answer === 'object' && !Array.isArray(answer)
    ? Object.fromEntries(Object.entries(answer).map(([key, value]) => [key, String(value ?? '')]))
    : {};
};

const getGroupStartClass = (
  config: DynamicTableConfig,
  column: DynamicTableColumnConfig,
  showGroupSeparators: boolean,
) => {
  if (!showGroupSeparators) return '';
  const groupIndex =
    config.groups?.findIndex(
      (group) => group.columnKeys[0] === column.key && group.columnKeys.includes(column.key),
    ) ?? -1;
  return groupIndex > 0 ? ' dynamic-table__group-start' : '';
};

export default function DynamicTableSummary({ question, answer, allAnswers = {} }: Props) {
  const values = useMemo(() => getValues(answer), [answer]);
  const config = useMemo(() => {
    if (!question.dynamicTable) return undefined;
    return resolveContinuousDynamicTableConfig(
      resolveDynamicTableRowsFromAnswer(
        filterDynamicTableRows(question.dynamicTable, allAnswers),
        allAnswers,
      ),
      values,
    );
  }, [allAnswers, question.dynamicTable, values]);
  const [tableSizings, setTableSizings] = useState<Record<string, DynamicTableSizing>>({});
  const [tableScrollbarWidth, setTableScrollbarWidth] = useState(0);
  const [tableOverflowByKey, setTableOverflowByKey] = useState<Record<string, boolean>>({});
  const tableRootRef = useRef<HTMLDivElement>(null);

  const updateTableViewports = useCallback(() => {
    const nextOverflowByKey: Record<string, boolean> = {};
    tableRootRef.current
      ?.querySelectorAll<HTMLElement>('[data-testid="dynamic-table-summary-viewport"]')
      .forEach((viewport) => {
        const tableKey = viewport.dataset.tableKey;
        if (tableKey) nextOverflowByKey[tableKey] = viewport.scrollWidth > viewport.clientWidth;
        positionBoundedGroupLabels(viewport);
      });
    setTableOverflowByKey((current) => {
      const keys = Object.keys(nextOverflowByKey);
      return keys.length === Object.keys(current).length &&
        keys.every((key) => current[key] === nextOverflowByKey[key])
        ? current
        : nextOverflowByKey;
    });
  }, []);

  useEffect(() => {
    if (!config || typeof document === 'undefined') return undefined;

    let cancelled = false;
    let frameId: number | undefined;
    const updateTableSizing = () => {
      if (cancelled) return;

      const measurement = document.createElement('span');
      measurement.setAttribute('aria-hidden', 'true');
      Object.assign(measurement.style, {
        position: 'absolute',
        visibility: 'hidden',
        whiteSpace: 'nowrap',
        fontFamily: 'GDS Transport, arial, sans-serif',
        fontSize: '16px',
        fontWeight: '700',
        lineHeight: '20px',
      });
      document.body.appendChild(measurement);
      const measureText = (text: string) => {
        measurement.textContent = text;
        return measurement.getBoundingClientRect().width;
      };

      const gutter = document.createElement('span');
      Object.assign(gutter.style, {
        position: 'absolute',
        visibility: 'hidden',
        width: '1cm',
      });
      document.body.appendChild(gutter);

      const scrollbar = document.createElement('span');
      Object.assign(scrollbar.style, {
        position: 'absolute',
        visibility: 'hidden',
        width: '100px',
        height: '100px',
        overflow: 'scroll',
        scrollbarGutter: 'stable',
      });
      document.body.appendChild(scrollbar);
      const scrollbarWidth = scrollbar.offsetWidth - scrollbar.clientWidth;
      const availableWidth = Math.max(
        document.documentElement.clientWidth -
          gutter.getBoundingClientRect().width * 2 -
          scrollbarWidth,
        0,
      );
      const columnSets = [
        config.columns,
        ...(config.groups ?? []).map((group) =>
          group.columnKeys
            .map((key) => config.columns.find((column) => column.key === key))
            .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
        ),
      ];
      const groupedSeparatorWidths = Object.fromEntries(
        (config.groups ?? []).slice(1).map((group) => [group.columnKeys[0], GROUP_SEPARATOR_WIDTH]),
      );
      const nextSizings = Object.fromEntries(
        columnSets.map((columns, index) => [
          getSizingKey(columns),
          calculateDynamicTableSizing({
            columns,
            rowLabels: config.rows.map((row) => row.label),
            rowHeader: config.rowHeader ?? 'Question',
            availableWidth,
            measureText,
            extraWidthByColumn: index === 0 ? groupedSeparatorWidths : undefined,
            headerLineLimit: config.headerLineLimit,
            showRowHeader: config.showRowHeader !== false,
          }),
        ]),
      );

      measurement.remove();
      gutter.remove();
      scrollbar.remove();
      setTableScrollbarWidth(scrollbarWidth);
      setTableSizings(nextSizings);
      frameId = window.requestAnimationFrame(() => {
        updateTableViewports();
      });
    };

    updateTableSizing();
    window.addEventListener('resize', updateTableSizing);
    document.fonts?.ready.then(updateTableSizing);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', updateTableSizing);
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
    };
  }, [config, updateTableViewports]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      updateTableViewports();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [tableSizings, updateTableViewports]);

  if (!config) return null;

  const showRowHeader = config.showRowHeader !== false;
  const calculatedRows = config.rows.filter((row) => row.calculation);
  const standardRows = config.rows.filter((row) => !row.calculation);
  const getColumnHint = (column?: DynamicTableColumnConfig) =>
    column ? interpolateQuestionText(column.hint, allAnswers) : undefined;

  const renderRows = (
    rows: DynamicTableRowConfig[],
    columns: DynamicTableColumnConfig[],
    showGroupSeparators: boolean,
  ) =>
    rows.map((row) => {
      const rowLabel =
        config.continuous && config.rowHeaderInput
          ? values[getDynamicTableRowHeaderFieldName(row.key)] || row.label
          : row.label;
      const rowHint = interpolateQuestionText(row.hint, allAnswers);
      return (
        <tr key={row.key} className="govuk-table__row">
          {showRowHeader && (
            <th
              scope="row"
              className={`govuk-table__header dynamic-table__row-header${
                row.calculation ? ' dynamic-table__total-label' : ''
              }`}
            >
              {rowLabel}
              {rowHint && (
                <div className="govuk-hint govuk-!-margin-top-1 govuk-!-margin-bottom-0">
                  {rowHint}
                </div>
              )}
            </th>
          )}
          {columns.map((column) => {
            const field = getDynamicTableFieldName(row.key, column.key);
            const value = formatDynamicTableCellValue(row.key, column, values);
            const isText = column.inputType === 'text' || column.inputType === 'duration';
            return (
              <td
                key={field}
                className={`govuk-table__cell${getGroupStartClass(
                  config,
                  column,
                  showGroupSeparators,
                )}`}
              >
                <span
                  className={`dynamic-table__input dynamic-table__read-only-value${
                    isText ? ' dynamic-table__input--text' : ''
                  }`}
                >
                  {value || '-'}
                </span>
              </td>
            );
          })}
        </tr>
      );
    });

  const renderTable = (
    columns: DynamicTableColumnConfig[],
    options: { caption?: string; showGroupHeaders?: boolean } = {},
  ) => {
    const sizingKey = getSizingKey(columns);
    const sizing = tableSizings[sizingKey];
    const hasHorizontalScroll = tableOverflowByKey[sizingKey] === true;
    const columnsWithHints = columns.filter((column) => getColumnHint(column));
    const displayedHint = getColumnHint(columnsWithHints[0]);
    const visibleGroups = (config.groups ?? [])
      .map((group) => ({
        ...group,
        columns: group.columnKeys
          .map((key) => columns.find((column) => column.key === key))
          .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
      }))
      .filter((group) => group.columns.length > 0);
    const tableClasses = [
      'govuk-table',
      config.compact ? 'dynamic-table--compact' : '',
      config.stickyHeaders ? 'dynamic-table--sticky-headers' : '',
      config.stickyRowHeader && showRowHeader ? 'dynamic-table--sticky-row-header' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <TableRegion
        className="govuk-!-margin-bottom-6"
        style={
          sizing
            ? ({
                '--dynamic-table-width': `${sizing.tableWidth + tableScrollbarWidth}px`,
              } as CSSProperties)
            : undefined
        }
      >
        {options.caption && (
          <TableCaption>
            <h3 className="govuk-heading-m dynamic-table__caption">{options.caption}</h3>
          </TableCaption>
        )}
        <TableViewport
          role="region"
          aria-label={`${options.caption ?? question.title} table`}
          tabIndex={0}
          data-testid="dynamic-table-summary-viewport"
          data-table-key={sizingKey}
          style={{ maxHeight: config.height }}
          onScroll={(event) => positionBoundedGroupLabels(event.currentTarget)}
        >
          <Table className={tableClasses} style={{ width: sizing?.tableWidth }}>
            <caption className="govuk-visually-hidden">{options.caption ?? question.title}</caption>
            {sizing && (
              <colgroup>
                {showRowHeader && <col style={{ width: sizing.rowHeaderWidth }} />}
                {columns.map((column) => (
                  <col key={column.key} style={{ width: sizing.columnWidths[column.key] }} />
                ))}
              </colgroup>
            )}
            <thead className="govuk-table__head">
              {options.showGroupHeaders && visibleGroups.length > 0 && (
                <tr className="govuk-table__row">
                  {showRowHeader && (
                    <th className="govuk-table__header dynamic-table__row-header" />
                  )}
                  {visibleGroups.map((group, groupIndex) => (
                    <th
                      key={group.key}
                      scope="colgroup"
                      colSpan={group.columns.length}
                      className={`govuk-table__header dynamic-table__group-header${
                        groupIndex > 0 ? ' dynamic-table__group-start' : ''
                      }`}
                    >
                      <span className="dynamic-table__group-label">
                        <span>{group.label}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              )}
              <tr className="govuk-table__row">
                {showRowHeader && (
                  <th scope="col" className="govuk-table__header dynamic-table__row-header">
                    {config.rowHeader ?? 'Question'}
                  </th>
                )}
                {columns.map((column) => {
                  const columnHint = getColumnHint(column);
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      className={`govuk-table__header dynamic-table__column-header${getGroupStartClass(
                        config,
                        column,
                        options.showGroupHeaders === true,
                      )}`}
                    >
                      {column.label}
                      {columnHint && <span className="govuk-visually-hidden">{columnHint}</span>}
                    </th>
                  );
                })}
              </tr>
              {columnsWithHints.length > 0 && (
                <tr className="govuk-table__row">
                  {showRowHeader && (
                    <th scope="row" className="govuk-table__header dynamic-table__hint-label">
                      Guidance
                    </th>
                  )}
                  <td colSpan={columns.length} className="govuk-table__cell dynamic-table__hint-cell">
                    {displayedHint}
                  </td>
                </tr>
              )}
            </thead>
            <tbody className="govuk-table__body">
              {renderRows(standardRows, columns, options.showGroupHeaders === true)}
            </tbody>
            {calculatedRows.length > 0 && (
              <tfoot>{renderRows(calculatedRows, columns, options.showGroupHeaders === true)}</tfoot>
            )}
          </Table>
        </TableViewport>
        {hasHorizontalScroll && (
          <p className="govuk-hint govuk-!-margin-top-2 govuk-!-margin-bottom-0">
            Use the bar to scroll left and right to see every column.
          </p>
        )}
      </TableRegion>
    );
  };

  return (
    <div ref={tableRootRef}>
      {config.layout === 'split' && config.groups?.length
        ? config.groups.map((group) => {
            const groupColumns = group.columnKeys
              .map((key) => config.columns.find((column) => column.key === key))
              .filter((column): column is DynamicTableColumnConfig => Boolean(column));
            return <div key={group.key}>{renderTable(groupColumns, { caption: group.label })}</div>;
          })
        : renderTable(config.columns, { showGroupHeaders: Boolean(config.groups?.length) })}
    </div>
  );
}
